<template>
  <div v-if="loading" class="text-center mt-5 font-2xl text-muted">
    <i class="fal fa-spinner fa-spin" /> Loading
  </div>
  <div v-else class="animated fadeIn">
    <customer-contact-modal @formSubmitted="submit" />
    <div class="my-5">
      <div class="display-4 font-3xl text-center">Describe your project.</div>
    </div>
    <b-row>
      <b-col cols="12">
        <b-form-group label="Customer" label-cols="2" invalid-feedback="Required" :state="!$v.step_one.customer_id.$invalid">
          <b-form-input v-model.lazy="filter" placeholder="Search for a customer..." debounce="300" />
          <b-alert v-if="!filter" show class="py-1 m-0 mt-3">{{ customers.length ? "Choose" : "Search" }} a customer.</b-alert>
          <b-row v-if="filter && !customers.length">
            <b-col cols="6" class="pr-2">
              <b-alert show class="py-1 m-0 mt-3">No matches found.</b-alert>
            </b-col>
            <b-col cols="6" class="pl-2">
              <b-button class="py-1 m-0 mt-3" block :variant="'success'" @click="handleAddClick">
                <i class="fal fa-plus" /> Add new customer
              </b-button>
            </b-col>
          </b-row>
          <custom-form-radio-group v-if="customers.length" v-model="step_one.customer_id" :options="[
            ...customers.map(i => ({
              text: i.short_name,
              value: i.id,
              icon: 'user'
            })),
            {
              text: 'Add customer'
            }
          ]" @change="value => stepOneUpdate(value, 'customer_id')" :state="!$v.step_one.customer_id.$invalid" class="mt-3" />
        </b-form-group>
      </b-col>
      <b-col cols="12">
        <b-form-group label="Contact" label-cols="2" invalid-feedback="Required" :state="!$v.step_one.customer_id.$invalid">
          <b-overlay v-if="loadingContactsAndLocations" show spinner-small class="py-3" />
          <b-row v-else-if="step_one.customer_id && !contacts.length">
            <b-col cols="6" class="pr-2">
              <b-alert show class="py-1 m-0 mt-3">No Contacts found.</b-alert>
            </b-col>
            <b-col cols="6" class="pl-2">
              <b-button class="py-1 m-0 mt-3" block :variant="'success'" @click="openModal">
                <i class="fal fa-plus" /> Add new Contact
              </b-button>
            </b-col>
          </b-row>
          <custom-form-checkbox-group v-if="contacts.length" :add_contact="true" v-model="step_one.contacts" :options="
            contacts.map(i => ({
              text: this.formatContactName(i),
              value: i.id,
              icon: 'id-card',
            }))
          " @change="value => stepOneUpdate(value, 'contacts')" @addnew="openModal" />
        </b-form-group>
      </b-col>
      <b-col cols="12" v-if="step_one.customer_id">
        <b-form-group label="Location" label-cols="2">
          <b-overlay v-if="loadingContactsAndLocations" show spinner-small class="py-3" />
          <custom-form-radio-group v-else-if="locations.length" v-model="step_one.location_id" :options="
            locations.map(i => ({
              text: i.fullAddress(),
              value: i.id,
              icon: 'home'
            }))
          " buttons @change="value => stepOneUpdate(value, 'location_id')" />
          <b-alert v-else-if="step_one.customer_id" show class="py-1 m-0">No customer locations available.</b-alert>
          <b-alert v-else show class="py-1 m-0">Select a customer to see available locations.</b-alert>
        </b-form-group>
      </b-col>
      <b-col cols="12">
        <b-form-group label="Type" label-cols="2" invalid-feedback="Required" :state="!$v.step_one.sample_type_id.$invalid">
          <custom-form-radio-group v-if="sampleTypes.length" v-model="step_one.sample_type_id" :options="
            sampleTypes.map(i => ({
              text: i.name,
              value: i.id,
              icon: 'cube'
            })).sort((a, b) => a.text.localeCompare(b.text))
          " :state="!$v.step_one.sample_type_id.$invalid" @change="value => stepOneUpdate(value, 'sample_type_id')" />
        </b-form-group>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import SampleTypeCollection from "@/collections/lab/SampleTypeCollection";
import CustomFormRadioGroup from "@/components/shared/form/CustomFormRadioGroup";
import CustomFormCheckboxGroup from "@/components/shared/form/CustomFormCheckboxGroup";
import CustomerContactModal from "@/components/shared/form/CustomerContactModal";
import ToastNotifications from "@/components/shared/mixins/ToastNotifications";
import CustomerModel from "@/models/crm/CustomerModel";
import { mapActions, mapGetters } from "vuex";
import { validationMixin } from "vuelidate/src";
import { required, minLength } from "vuelidate/lib/validators";
import { getCollection } from "@/shared/fetch-utils";
import { CrmRepositoryFactory } from "@/repositories/crm/CrmRepositoryFactory";

const CustomerContactsRepository = CrmRepositoryFactory.get("customerContacts");
export default {
  name: "ProjectWizardStep1",
  components: {
    CustomFormRadioGroup,
    CustomFormCheckboxGroup,
    CustomerContactModal,
  },
  mixins: [validationMixin, ToastNotifications],
  props: {
    sampleTypes: {
      type: Object,
      default: () => new SampleTypeCollection()
    },
    project: {
      type: Object,
      default: () => null
    }
  },
  data() {
    return {
      loadingContactsAndLocations: false,
      customers: [],
      contacts: [],
      locations: [],
      loading: false,
    };
  },
  computed: {
    ...mapGetters(["projectFormStepOne"]),
    step_one() {
      return this.projectFormStepOne;
    },
    filter: {
      get() {
        return this.step_one.filter;
      },
      set(value) {
        this.UPDATE_STEP_ONE({ value, name: "filter" });
        this.loadCustomers();
      }
    }
  },
  validations: {
    step_one: {
      customer_id: { required },
      contacts: { required, minLength: minLength(1) },
      sample_type_id: { required }
    }
  },
  methods: {
    ...mapActions(["UPDATE_STEP_ONE"]),
    stepOneUpdate(value, name) {
      if (name === "customer_id") {
        this.loadContactsAndLocations(value);
      }
      this.UPDATE_STEP_ONE({ value, name });
    },
    handleAddClick() {
      this.$router.push("/customer/add");
    },
    displayError({ response }) {
      const { status, data } = response;

      this.$bvToast.toast(`Code: ${status}. ${data.error}`, {
        title: "Server Error.",
        variant: "danger"
      });
    },
    async loadCustomers() {
      try {
        const collection = await getCollection({
          collection_type: "customer",
          filters: [
            {
              logic: "filterContains",
              field: "short_name",
              value: this.filter
            },
            {
              logic: "setLimit",
              value: 10
            }
          ]
        });

        this.customers = collection.models;
      } catch ({ response }) {
        this.displayError(response);
      }
    },
    async loadContactsAndLocations(customer_id) {
      if (!this.customers.find(({ id }) => id === customer_id)) {
        let customer = new CustomerModel({ id: customer_id });
        customer.fetch().then(() => {
          this.customers.push(customer);
        });
      }

      this.loadingContactsAndLocations = true;

      const filters = [
        {
          logic: "filterEquals",
          field: "customer_id",
          value: customer_id

        }
      ];

      try {
        const contacts = await getCollection({
          collection_type: "customer_contact",
          filters,
          message: this.$bvToast
        });
        const locations = await getCollection({
          collection_type: "customer_location",
          filters,
          message: this.$bvToast
        });

        this.contacts = contacts.models;
        this.locations = locations.models;
        this.loadingContactsAndLocations = false;

        // set default value if only one contact
        if (this.contacts.length === 1) {
          this.step_one.contacts = [this.contacts[0].id];
          this.stepOneUpdate([this.contacts[0].id], 'contacts');
        }

        // set default value if only one location
        if (this.locations.length === 1) {
          this.step_one.location_id = this.locations[0].id;
          this.stepOneUpdate(this.locations[0].id, 'location_id');
        }
      } catch ({ response }) {
        this.displayError(response);
      }
    },
    /**
     * format contact name
     * @param {*} contact_data 
     */
    formatContactName(contact_data) {

      const parts = [];

      if (contact_data.first_name) {
        parts.push(contact_data.first_name);
      }
      if (contact_data.last_name) {
        parts.push(contact_data.last_name);
      }
      if (contact_data.email) {
        if (parts.length) {
          parts.push("" + contact_data.email + "");
        }
        else {
          parts.push(contact_data.email);
        }
      }

      return parts.join(' ')
    },
    openModal() {
      this.$bvModal.show('customer-contact-modal');
    },
    closeModal() {
      this.$bvModal.hide('customer-contact-modal');
    },
    async submit(formData) {
      try {
        await CustomerContactsRepository.create({
          customer_id: this.step_one.customer_id,
          ...formData,
        });
      } catch ({ response }) {
        const { status, data } = response;

        this.$bvToast.toast(`Code: ${status}. ${data.error}`, {
          title: "Server Error.",
          variant: "danger",
        });
      }
      await this.loadContactsAndLocations(this.step_one.customer_id);
      this.closeModal();
    },
    async init () {
      if (this.project && this.project.id) {
        this.step_one.customer_id = this.project.customer_id
        this.stepOneUpdate(this.project.customer_id, 'customer_id')

        const collection = await getCollection({
          collection_type: 'customer',
          filters: [
            {
              logic: 'filterEquals',
              field: 'id',
              value: this.project.customer_id
            },
            {
              logic: 'setLimit',
              value: 10
            }
          ]
        })

        this.customers = collection.models

        await this.loadContactsAndLocations(this.project.customer_id)

        this.step_one.contacts = [this.project.customer_contact_id]
        this.stepOneUpdate([this.project.customer_contact_id], 'contacts')
        this.step_one.location_id = this.project.customer_location_id
        this.stepOneUpdate(this.project.customer_location_id, 'location_id')
      }

      this.loading = false
    }
  },
  mounted() {
    this.loading = true;

    this.filter && this.loadCustomers();
    this.step_one.customer_id &&
      this.loadContactsAndLocations(this.step_one.customer_id);

    this.init()
  }
};
</script>